import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentsService } from '../services/paymentsService';

/**
 * Stripe Checkout success-redirect page.
 *
 * Stripe redirects here with ?session_id=cs_xxx after the customer completes payment.
 * We poll the backend's /subscription endpoint until status flips to 'active' or 'trialing'
 * (the webhook does the actual update). Then we navigate back to /payments.
 */
const PaymentsReturnPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [message, setMessage] = useState<string>(
    sessionId ? 'Confirming your subscription…' : 'No checkout session id provided',
  );
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12; // 12 * 2s = 24s

    const tick = async () => {
      attempts++;
      try {
        const { subscription } = await paymentsService.getSubscription();
        if (cancelled) return;
        if (subscription && ['active', 'trialing', 'past_due'].includes(subscription.status)) {
          setMessage('Subscription active. Redirecting…');
          setTimeout(() => navigate('/payments?status=success'), 800);
          return;
        }
      } catch {
        /* ignore transient errors */
      }
      if (attempts >= maxAttempts) {
        setIsError(true);
        setMessage(
          'Subscription update is taking longer than expected. The webhook may not be configured yet — refresh the payments page in a moment.',
        );
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate]);

  return (
    <div className="p-12 max-w-xl mx-auto text-center">
      <div className="text-2xl font-semibold text-[var(--color-text-primary)] mb-2">
        {isError ? 'Almost there' : 'Thanks!'}
      </div>
      <div className="text-sm text-[var(--color-text-secondary)]">{message}</div>
      <button
        type="button"
        onClick={() => navigate('/payments')}
        className="mt-6 px-4 py-2 text-sm rounded-md border border-[var(--color-border-default)] hover:bg-[var(--color-background-surface)]"
      >
        Back to billing
      </button>
    </div>
  );
};

export default PaymentsReturnPage;
